import type { Modal } from 'uikit';
import { getElementCoordinatesBySelector } from 'utils/dom';

import { debounce } from 'debounce';
import type { ComponentProps } from 'react';
import { useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { IModalDialogProps } from '../ModalDialog';

export const useContainerDock = (props: IModalDialogProps & Omit<ComponentProps<typeof Modal>, 'title'>) => {
    const { isDockedToContainer, containerRef, rootClassName, dockedToWindow } = props;

    const dockedRootClass = useMemo(
        () => (isDockedToContainer || dockedToWindow ? `docked-modal-${uuidv4()}` : ''),
        [dockedToWindow, isDockedToContainer],
    );

    const actualRootClassName = useMemo(
        () => `${rootClassName ? `${rootClassName} ` : ''}${dockedRootClass}`,
        [dockedRootClass, rootClassName],
    );

    useEffect(() => {
        let applyParams: () => any;

        if (isDockedToContainer || dockedToWindow) {
            const { current } = containerRef || {};
            const modalWindow = document.body.querySelector(`.${dockedRootClass} .Modal-Window`) as HTMLElement;

            const applyParentParams = debounce(() => {
                const coordinates = getElementCoordinatesBySelector(current);

                modalWindow.style.top = `${coordinates?.y || 0}px`;
                modalWindow.style.width = `${current?.clientWidth || 0}px`;
                modalWindow.style.height = `${current?.clientHeight || 0}px`;
            }, 50);

            const applyWindowParams = debounce(() => {
                if (dockedToWindow) {
                    modalWindow.style[dockedToWindow] = '0';

                    if (['bottom', 'top'].includes(dockedToWindow)) {
                        modalWindow.style.width = `${document.body.clientWidth}px`;
                    } else {
                        modalWindow.style.height = `${document.body.clientHeight}px`;
                    }
                }
            }, 50);

            if (modalWindow) {
                if (modalWindow.parentElement) {
                    modalWindow.parentElement.style.padding = 'unset';
                }

                modalWindow.style.maxWidth = '100%';
                modalWindow.style.maxHeight = '100%';

                applyParams = current ? applyParentParams : applyWindowParams;

                applyParams();

                window.addEventListener('resize', applyParams);
                window.addEventListener('scroll', applyParams);
            }
        }

        return () => {
            window.removeEventListener('resize', applyParams);
            window.removeEventListener('scroll', applyParams);
        };
    }, [containerRef, dockedRootClass, dockedToWindow, isDockedToContainer]);

    return actualRootClassName;
};
